import { FloatingFormItem, InputDecimal } from '@/components/_common';
import {
  KitCreateFormValues,
  KitItemType,
  KitItemCreate,
} from '@/core/domain/operational/Kits/Kits.types';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
  useWindowSize,
} from '@/hooks';
import {
  Form,
  Row,
  Col,
  Input,
  Button,
  Dropdown,
  Typography,
  Menu,
  Alert,
} from 'antd';
import KitsItemsList from '../KitsItemsList';
import SelectPartsListModal from '@/components/_common/SelectPartsListModal';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import SelectTasksListModal from '@/components/_common/SelectTasksListModal';
import { Task } from '@/core/domain/operational/Tasks/Tasks.types';
import { useKitItemsContext } from '@/pages/OperationalPages/KitsPage/KitsCreatePage';
import { useCompaniesReduced } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useKitItemsContextEdit } from '@/pages/OperationalPages/KitsPage/KitDetailPage';
import { WorkOrderKit } from '@/core/domain/workorder/Kits/Kits.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  isEdit?: boolean;
  kit?: WorkOrderKit;
}

const KitsForm: React.FC<Props> = ({ isEdit = false, kit }) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const { isMobile } = useWindowSize();
  const form = Form.useFormInstance<KitCreateFormValues>();
  const { items, setItems } = isEdit
    ? useKitItemsContextEdit()
    : useKitItemsContext();
  const { data: companiesData } = useCompaniesReduced({
    rows: 1000,
    isCompanyFilter: true,
  });
  const companies = flatInfiniteData(companiesData);

  const [modalPartsOpen, modalPartsAction] = useDisclosure();
  const [modalTasksOpen, modalTasksAction] = useDisclosure();

  const increaseDecreaseWatch = Form.useWatch('increaseDecrease', form);

  const selectItemsOptions = (
    <Menu
      items={[
        {
          key: 'parts',
          label: 'Peças',
          onClick: modalPartsAction.open,
        },
        {
          key: 'tasks',
          label: 'Serviços',
          onClick: modalTasksAction.open,
        },
      ]}
    />
  );

  const handleSelectTasks = (tasks: Task[]) => {
    if (tasks?.length) {
      setItems((prev) => [
        ...prev,
        ...tasks
          .filter(
            (task) =>
              !!task?.id &&
              !prev.some(
                (item) =>
                  item.itemType === KitItemType.task && item.itemId === task.id,
              ),
          )
          .map(
            (task) =>
              ({
                itemId: task.id,
                itemType: KitItemType.task,
                description: task.description,
                temparioCode: task.temparioCode,
                quantity: 1,
                increaseDecrease: increaseDecreaseWatch || undefined,
                companies: task.taskPriceSet?.map((item) => ({
                  name: companies.find(
                    (company) => company.id === item.companyId,
                  )?.name,
                  id: isEdit ? item.id : undefined,
                  companyId: item.companyId,
                  price: item.price,
                  unitPrice: 0,
                  increaseDecrease: increaseDecreaseWatch || 0,
                })),
              } as KitItemCreate),
          ),
      ]);
    }
  };

  const handleSelectParts = (parts: StockItem[]) => {
    if (parts?.length) {
      setItems((prev) => [
        ...prev,
        ...parts
          .filter(
            (part) =>
              !prev.some(
                (item) =>
                  item.itemType === KitItemType.part &&
                  item.itemId === part.partId,
              ),
          )
          .map((part) => ({
            itemId: part.partId,
            itemType: KitItemType.part,
            description: part.description,
            barcode: part.barcode,
            originalCode: part.originalCode,
            sku: part.sku,
            manufacturingCode: part.manufacturingCode,
            companyCode: part.companyCode,
            manufacturer: part.manufacturer,
            quantity: 1,
            increaseDecrease: increaseDecreaseWatch || undefined,
            companies: part.prices.map((price) => ({
              companyId: price.company.id,
              name: price.company.name,
              price: price.price,
              unitPrice: 0,
              increaseDecrease: increaseDecreaseWatch || 0,
            })),
          })),
      ]);
    }
  };

  const handleIncreaseDecrease = (value: number) => {
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        increaseDecrease: value || undefined,
        companies: item.companies.map((company) => ({
          ...company,
          increaseDecrease: value || undefined,
        })),
      })),
    );
  };

  return (
    <>
      <Form form={form}>
        <Row gutter={[12, 12]}>
          <Col span={24} md={16} lg={18}>
            <FloatingFormItem
              name="description"
              label="Descrição"
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(200)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              name="increaseDecrease"
              label="Acréscimo/Decréscimo (%)"
              getValueFromEvent={(value) => {
                handleIncreaseDecrease(Number(value));
                return value;
              }}
            >
              <InputDecimal
                disabled={
                  !items?.length ||
                  !hasPermissions([Authorities.KITS_CHANGE_PRICES])
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Row align="middle">
              <Col flex={1}>
                <Typography.Title level={2}>Itens</Typography.Title>
              </Col>

              <Col span={isMobile ? 24 : undefined}>
                <Dropdown
                  overlay={selectItemsOptions}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button type="primary" block>
                    Adicionar Item
                  </Button>
                </Dropdown>
              </Col>
            </Row>
          </Col>

          {isEdit && items.length < (kit?.items.length || 0) && (
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                message="O item será removido ao ser salvo."
              />
            </Col>
          )}

          <Col span={24}>
            <KitsItemsList isEdit={isEdit} />
          </Col>
        </Row>
      </Form>

      <SelectPartsListModal
        itemSelectedIds={
          items
            ?.filter((item) => item.itemType === KitItemType.part)
            .map((item) => item.itemId) || []
        }
        onCallback={handleSelectParts}
        open={modalPartsOpen}
        onCancel={modalPartsAction.close}
      />

      <SelectTasksListModal
        itemSelectedIds={
          items
            ?.filter((item) => item.itemType === KitItemType.task)
            .map((item) => item.itemId) || []
        }
        open={modalTasksOpen}
        onCancel={modalTasksAction.close}
        onCallback={handleSelectTasks}
      />
    </>
  );
};

export default KitsForm;
